import { IPublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useMsal } from "@azure/msal-react";
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { User, UserRole, getUserRole } from "../models/user.model";
import { AdministrationService } from "../services/administrationService";
import { TechnologyCategoryService } from "../services/technologyCategoryService";
import { GlobalErrorHandler } from "../utils/globalErrorHandler";
import { TimeoutErrorHandler } from "../utils/timeoutErrorHandler";
import { tokenAcquisitionQueue } from "../utils/tokenAcquisitionQueue";
import { TokenExpirationHandler } from "../utils/tokenExpirationHandler";
import { ENABLE_ADMIN_CHECK, apiScopes } from "./msalConfig";

/**
 * AUTHENTICATION ARCHITECTURE OVERVIEW
 * =====================================
 * 
 * This AuthProvider handles the main application authentication flow using MSAL.
 * However, there are ADDITIONAL authentication flows that happen in other services:
 * 
 * 1. GRAPH SERVICE AUTHENTICATION (src/services/graphService.ts):
 *    - Handles Microsoft Graph API calls (User.Read scope)
 *    - Has its own token acquisition logic with consent handling
 *    - Automatically prompts for consent if AADSTS65001 error occurs
 *    - Falls back to interactive token acquisition when needed
 *    - Used for: user profile photos, additional user info
 * 
 * 2. BACKEND API AUTHENTICATION:
 *    - Uses the main MSAL token from this AuthProvider
 *    - Scope: defined in REACT_APP_AZURE_API_SCOPE environment variable
 *    - Used for: all backend API calls (CV data, user management, etc.)
 * 
 * MSAL ERROR HANDLING STRATEGY:
 * =============================
 * 
 * Current approach (after fixing consent issues):
 * - Session expiration handling is RE-ENABLED and working properly
 * - The original login issues were caused by consent problems, not session expiration
 * - TokenExpirationHandler.handleTokenExpiration() calls are active
 * - Comprehensive MSAL info logging remains enabled for monitoring
 * - Proper distinction between genuine token expiration and backend HTTP errors
 * 
 * Common MSAL Errors and Their Handling:
 * - AADSTS65001: User/admin consent required → GraphService handles with popup
 * - InteractionRequiredAuthError: Token expired → Proper session expiration handling
 * - HTTP 400/404: Backend errors → Not treated as auth failures
 * - HTTP 401/403: Permission errors → Not treated as token expiration
 * 
 * TROUBLESHOOTING MSAL ISSUES:
 * ============================
 * 
 * 1. Check console logs for "[Auth] MSAL Info" and "[GraphService] MSAL Info" messages
 * 2. Verify app registration permissions in Azure Portal
 * 3. Check if admin consent is granted for the tenant
 * 4. For consent issues, GraphService will automatically prompt users
 * 5. Backend API errors (4xx/5xx) are NOT authentication failures
 * 6. Session expiration is handled automatically with proper token refresh
 * 
 * IMPORTANT NOTE ON ADMIN CONSENT:
 * ===============================
 * Even when "Grant admin consent for [Your Tenant]" is set in Azure Portal,
 * individual users may still get AADSTS65001 consent errors for Microsoft Graph API calls.
 * This can happen due to:
 * - Conditional access policies
 * - User-specific consent requirements
 * - Different scopes being requested (User.Read vs backend API scope)
 * - Token cache issues
 * 
 * The GraphService handles this by automatically prompting for interactive consent
 * when AADSTS65001 occurs, which resolves the issue for that user going forward.
 * 
 * SESSION STORAGE vs LOCAL STORAGE:
 * ==================================
 * - MSAL cacheLocation configured to use localStorage (NOT sessionStorage)
 * - Reason: localStorage is shared across browser tabs, sessionStorage is not
 * - This enables multi-tab authentication without re-login
 * - Trade-off: Tokens persist after tab close (less secure) vs better UX (multi-tab support)
 * - Security: MSAL tokens have expiration times and are refreshed automatically
 *
 * DEBUGGING HISTORY:
 * =================
 * - Original issue: Users couldn't login due to AADSTS65001 consent errors
 * - Temporary fix: Disabled session expiration to isolate the problem
 * - Root cause: Microsoft Graph API consent issues despite admin consent
 * - Final solution: Added automatic consent handling in GraphService
 * - Result: Session expiration handling re-enabled and working properly
 */

// Auth context type definition
interface AuthContextType {
  isAuthenticated: boolean;
  isInitialized: boolean;
  isAdmin: boolean;
  isManager: boolean;
  user: any | null;
  currentUser: User | null;
  userRole: UserRole | null;
  login: () => Promise<void>;
  loginWithRedirect: () => Promise<void>;
  logout: () => Promise<void>;
  inProgress: string;
  updateUserRole: (userData: User) => void;
}

// Create auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  instance: IPublicClientApplication;
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ instance, children }) => {
  return (
    <MsalProvider instance={instance}>
      <AuthContextProvider>
        {children}
      </AuthContextProvider>
    </MsalProvider>
  );
};

// Internal context provider with the actual implementation
const AuthContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isManager, setIsManager] = useState<boolean>(false);
  const [user, setUser] = useState<any | null>(accounts[0] || null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const administrationService = useMemo(() => new AdministrationService(), []);

  const getLoginRequest = useCallback(() => {
    const fallbackScopes = ["openid", "profile"];

    return {
      scopes: apiScopes.backendApi.length > 0 ? apiScopes.backendApi : fallbackScopes,
    };
  }, []);

  const isPopupInteractionError = (error: unknown) => {
    const message = (error instanceof Error ? error.message : String(error)).toLowerCase();

    return (
      message.includes("popup_window_error") ||
      message.includes("popup_window_timeout") ||
      message.includes("user_cancelled") ||
      message.includes("monitor_window_timeout") ||
      message.includes("block")
    );
  };

  // Fetch current user data from the backend
  const fetchCurrentUser = useCallback(async () => {
    if (!accounts.length) return;

    try {
      // Clear cache to ensure we get fresh data from the new endpoint
      administrationService.clearCache();
      const userData = await administrationService.getCurrentPerson();
      setCurrentUser(userData);
      setIsAdmin(userData.isAdmin);
      setIsManager(userData.isManager);
      setUserRole(getUserRole(userData));
      return userData;
    } catch (error) {
      console.error('[Auth] Error fetching current user:', error);

      // MSAL INFO LOGGING: Log what happened and what error was returned
      const errorMessage = error instanceof Error ? error.message : String(error);
      const hasHttpStatus = /\b[4-5]\d\d\b/.test(errorMessage) || errorMessage.includes('HTTP error');
      const isTimeoutError = TimeoutErrorHandler.isTimeoutError(error);

      console.log('[Auth] MSAL Info - Error details:', {
        error: errorMessage,
        hasHttpStatus,
        isTimeoutError,
        isTokenExpirationError: TokenExpirationHandler.isTokenExpirationError(error)
      });

      // Check for iframe timeout errors specifically
      if (isTimeoutError) {
        console.log('[Auth] Detected monitor_window_timeout while fetching user data, handling specially');
        await TimeoutErrorHandler.handleTimeoutError(error);
        return null;
      }

      // Session expiration handling - now re-enabled since consent issue was the real problem
      if (!hasHttpStatus && TokenExpirationHandler.isTokenExpirationError(error)) {
        console.log('[Auth] Confirmed genuine MSAL token expiration while fetching user data');
        await TokenExpirationHandler.handleTokenExpiration(error);
        return null;
      }

      // All other errors (including HTTP errors) are backend issues, not auth issues
      console.log('[Auth] Backend error while fetching user data - NOT treating as token expiration');
      return null;
    }
  }, [accounts, administrationService]);

  // Check if the user is in the admin group
  const checkAdminStatus = useCallback(async () => {
    if (!accounts.length) {
      setIsAdmin(false);
      setIsManager(false);
      sessionStorage.removeItem('isAdmin');
      sessionStorage.removeItem('isManager');
      return false;
    }

    // If admin check is disabled, treat all users as admins
    if (!ENABLE_ADMIN_CHECK) {
      setIsAdmin(true);
      setIsManager(true);
      sessionStorage.setItem('isAdmin', 'true');
      sessionStorage.setItem('isManager', 'true');
      return true;
    }

    try {
      const response = await tokenAcquisitionQueue.acquireTokenSilent(instance, {
        scopes: apiScopes.backendApi,
        account: accounts[0]
      });

      // After getting the token, fetch the user data
      const userData = await fetchCurrentUser();

      // If fetchCurrentUser failed but we have a token, set default values
      if (!userData) {
        setIsAdmin(false);
        setIsManager(false);
        sessionStorage.removeItem('isAdmin');
        sessionStorage.removeItem('isManager');
      }

      // Use the token for API calls
      return response.accessToken;
    } catch (error) {
      console.error('[Auth] Error during admin status check:', error);

      // MSAL INFO LOGGING: Log what happened and what error was returned
      const errorMessage = error instanceof Error ? error.message : String(error);
      const hasHttpStatus = /\b[4-5]\d\d\b/.test(errorMessage) || errorMessage.includes('HTTP error');
      const isTimeoutError = TimeoutErrorHandler.isTimeoutError(error);

      console.log('[Auth] MSAL Info - Admin check error details:', {
        error: errorMessage,
        hasHttpStatus,
        isTimeoutError,
        isTokenExpirationError: TokenExpirationHandler.isTokenExpirationError(error)
      });

      // Check for iframe timeout errors specifically
      if (isTimeoutError) {
        console.log('[Auth] Detected monitor_window_timeout during admin check, handling specially');
        await TimeoutErrorHandler.handleTimeoutError(error);
        return false;
      }

      // Session expiration handling - now re-enabled since consent issue was the real problem
      if (!hasHttpStatus && TokenExpirationHandler.isTokenExpirationError(error)) {
        console.log('[Auth] Confirmed genuine MSAL token expiration during admin check');
        await TokenExpirationHandler.handleTokenExpiration(error);
        return false;
      }

      // All other errors (including HTTP errors) are backend issues, not auth issues
      console.log('[Auth] Backend error during admin check - NOT treating as token expiration, setting default permissions');
      // Reset admin/manager status on any backend error
      setIsAdmin(false);
      setIsManager(false);
      sessionStorage.removeItem('isAdmin');
      sessionStorage.removeItem('isManager');
      return false;
    }
  }, [accounts, instance, fetchCurrentUser]);

  useEffect(() => {
    const checkAuth = async () => {
      // If the user is already authenticated
      if (accounts.length > 0) {
        console.log('[Auth] Accounts detected after render, setting up authentication');
        setUser(accounts[0]);

        // If admin check is disabled, set admin status immediately
        if (!ENABLE_ADMIN_CHECK) {
          setIsAdmin(true);
          setIsManager(true); // Also set as manager for broader access
          sessionStorage.setItem('isAdmin', 'true');
          sessionStorage.setItem('isManager', 'true');
        }

        // Check admin status on load - add small delay for first-time users
        try {
          // Small delay to allow any pending login operations to complete
          await new Promise(resolve => setTimeout(resolve, 100));
          // 20-second safety timeout: if checkAdminStatus hangs (e.g. acquireTokenSilent
          // never resolves due to a network stall) we still unblock the loading screen.
          // The token-queue timeout (25 s) should fire first and throw, but this is a
          // belt-and-suspenders guard.
          const adminCheckTimeout = new Promise<void>((resolve) =>
            setTimeout(() => {
              console.warn('[Auth] checkAdminStatus timed out — forcing isInitialized=true');
              resolve();
            }, 20_000)
          );
          await Promise.race([checkAdminStatus(), adminCheckTimeout]);
        } catch (error) {
          console.error('[Auth] Error checking admin status:', error);

          // MSAL INFO LOGGING: Log what happened
          console.log('[Auth] MSAL Info - Auth check error details:', {
            error: error instanceof Error ? error.message : String(error),
            status: (error as any)?.status || (error as any)?.statusCode
          });

          // For new users or data issues, don't treat as auth failure
          const status = (error as any)?.status || (error as any)?.statusCode;
          if (status >= 400 && status < 500) {
            setIsAdmin(false);
            setIsManager(false);
            sessionStorage.removeItem('isAdmin');
            sessionStorage.removeItem('isManager');
          }
        }
      } else {
        // Ensure state is reset when no accounts are present
        if (user || currentUser || isAdmin || isManager) {
          console.log('[Auth] No accounts detected, resetting auth state');
          setUser(null);
          setCurrentUser(null);
          setUserRole(null);
          setIsAdmin(false);
          setIsManager(false);
        }

        // For new users, don't try silent login as there's no account to use
        // The user will need to explicitly click the login button
        // This prevents the "no_account_error" for first-time users
      }

      // Mark initialization as complete
      setIsInitialized(true);
    };

    checkAuth();
  }, [accounts, instance, checkAdminStatus]);

  // Re-enabled: Proactive token management for authenticated users
  // Initialize proactive token management for authenticated users
  useEffect(() => {
    let cleanupTokenRefresh: (() => void) | null = null;

    if (accounts.length > 0) {
      // Set up periodic token refresh to prevent expiration issues
      cleanupTokenRefresh = TokenExpirationHandler.setupPeriodicTokenRefresh();

      // Set up global error handlers to catch unhandled auth errors
      GlobalErrorHandler.setupGlobalHandlers();

      // Check URL parameters for session expiration
      const urlParams = new URLSearchParams(window.location.search);
      const needsInteractiveLogin = urlParams.get('interactiveLogin') === 'true';

      // If coming back after a timeout with the interactive flag, perform interactive login
      if (needsInteractiveLogin) {
        // Remove the parameter to prevent loops
        urlParams.delete('interactiveLogin');
        const newSearch = urlParams.toString() ? '?' + urlParams.toString() : '';
        const newUrl = window.location.pathname + newSearch;

        window.history.replaceState({}, document.title, newUrl);

        // Attempt login after a short delay to let the page load
        setTimeout(() => {
          console.log('[Auth] Detected interactiveLogin parameter, initiating login redirect');
          instance.loginRedirect(getLoginRequest())
            .then(() => console.log('[Auth] Interactive login redirect initiated after timeout'))
            .catch(error => console.error('[Auth] Interactive login redirect failed after timeout:', error));
        }, 1000);
      }
    }

    return () => {
      if (cleanupTokenRefresh) {
        cleanupTokenRefresh();
      }
    };
  }, [accounts.length, instance, getLoginRequest]);

  const loginWithRedirect = async () => {
    try {
      await instance.loginRedirect(getLoginRequest());
    } catch (error) {
      console.error('[Auth] loginRedirect failed', error);
    }
  };

  const login = async () => {
    const loginRequest = getLoginRequest();

    try {
      const result = await instance.loginPopup(loginRequest);
      if (result.account) {
        // Set user immediately — the checkAuth effect will handle admin status
        // once MSAL fires the accounts-changed event (avoids double checkAdminStatus call)
        setUser(result.account);

        console.log('[Auth] MSAL Info - Login successful:', {
          account: result.account.username,
          scopes: apiScopes.backendApi
        });
      }
    } catch (error) {
      console.error("[Auth] Login popup failed", error);

      if (isPopupInteractionError(error)) {
        console.warn("[Auth] Popup interaction failed, falling back to redirect login");

        try {
          await instance.loginRedirect(loginRequest);
          return;
        } catch (redirectError) {
          console.error("[Auth] Login redirect failed", redirectError);
        }
      }

      // MSAL INFO LOGGING: Log login failure details
      console.log('[Auth] MSAL Info - Login failed:', {
        error: error instanceof Error ? error.message : String(error),
        errorType: error instanceof Error ? error.constructor.name : typeof error
      });
    }
  };

  const logout = async () => {
    // Clear all React state
    setUser(null);
    setCurrentUser(null);
    setUserRole(null);
    setIsAdmin(false);
    setIsManager(false);

    // Clear session storage auth keys
    sessionStorage.removeItem('isAdmin');
    sessionStorage.removeItem('isManager');

    // Clear administration service cache
    AdministrationService.clearCache();

    // Clear other service caches
    try {
      // Clear project roles cache
      const { ProjectRolesService } = await import('../features/CVEditor/features/ProjectExperience/services/projectRolesService');
      ProjectRolesService.clearCache();
    } catch (error) {
      console.warn('[Auth] Could not clear ProjectRolesService cache:', error);
    }

    try {
      // Clear technology category cache
      const techCategoryService = new TechnologyCategoryService();
      techCategoryService.clearAllCache();
    } catch (error) {
      console.warn('[Auth] Could not clear TechnologyCategoryService cache:', error);
    }

    // Clear MSAL's token cache locally.
    // We intentionally skip logoutPopup() / logoutRedirect() because all users
    // are on domain-joined machines — Azure AD SSO would silently re-authenticate
    // them immediately anyway, making a full IdP logout pointless.
    // clearCache() ends the app session without a popup or round-trip to Microsoft.
    await instance.clearCache();

    // Clear local storage auth-related items after MSAL logout
    const authKeys = Object.keys(localStorage).filter(key =>
      key.includes('msal') ||
      key.includes('auth') ||
      key.includes('token')
    );

    authKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    // clearCache() does not fire MSAL React account-change events, so the
    // ProtectedRoute guard would not re-evaluate without a page reload.
    window.location.href = '/';
  };

  // Function to allow updating user role from other components
  const updateUserRole = useCallback((userData: User) => {
    if (!userData) return;

    setCurrentUser(userData);
    setIsAdmin(userData.isAdmin);
    setIsManager(userData.isManager);
    setUserRole(getUserRole(userData));
  }, []);

  const contextValue: AuthContextType = {
    isAuthenticated: accounts.length > 0,
    isInitialized,
    isAdmin,
    isManager,
    user,
    currentUser,
    userRole,
    login,
    loginWithRedirect,
    logout,
    inProgress,
    updateUserRole
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to access authentication state
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 
